import React, { useEffect, useState } from 'react';
import { Hidden } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';
import Loader from '@cpg-its/ads.common.loading-circle';
import Banner from '@cpg-its/ads.containers.banner';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { useInstitutionSetting } from '../../../components/use-institution-settings';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { InstitutionState } from '../institution-model';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { ExternalLink } from '../../../../../global/components/external-link-modal';
import { compensationPollJob, getBatchCompensations, uploadCompensationFile, validateCompensationFile } from '../../../../../global/ducks/benadmin-api';
import { downloadFileFromApi } from '../../../../../global/helpers/file-helpers';
import { ApiEndpoint } from '../../../../../global/constants/api-endpoints';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { LabelAndValue } from '../../../../../global/components/data-display/label-value';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { GuidedProcessSupportProps } from '../../../models/arc-epi-props';

const compensationFileName = 'Institution-Compensation.xls';

interface ValidateState {
	errors: number;
	file_name: string
	no_changes: number;
	success: number;
	warnings: number;
}

function InstitutionCompensationApp() {

	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);

	const settings = useInstitutionSetting();
	const dispatch = useDispatch();
	const [validateState, setValidateState] = useState<ValidateState>(null);
	const [feedBackFile, setFeedBackFile] = useState(null);
	const [uploadSuccess, setUploadSuccess] = useState(null);
	const [stagedFile, setStagedFile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [failedProcessing, setfailedProcessing] = useState(false);
	const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.Compensation}`);
	const token = useSelector<TcsmStates, string>(state => state.authenticatedState?.accessToken);
	const { state } = useLocation<any>();
	const hasValidateError = validateState?.errors > 0;
	const buttonClasses = buttonStyles();

	useEffect(() => {
		dispatch(apiErrorClear());
	}, []);


	if (!institutionState?.institutionClientNumber) {
		return (
			<Redirect to=
				{
					{
						pathname: institutionSelect,
						state,
					}
				}
			/>);
	}

	if (!institutionState?.currentInstitution) {
		return (<Loader />);
	}

	const getPollJobStatus = (data) => {
		dispatch(compensationPollJob(
			data.poll_url,
			(response) => {
				if (response?.data?.status === 'complete') {
					setfailedProcessing(false);
					downloadFileFromApi(
						`${ApiEndpoint.benAdmin}/employers/${institutionState.currentInstitution?.org_client_nbr}/batch_compensations/${data.poll_url?.split('=')[1]}`,
						token,
						response?.data?.results?.file_name || compensationFileName);
				} else if (response?.data?.status === 'failed') {
					setfailedProcessing(true);
				} else {
					getPollJobStatus(data);
				}


			}));
	};


	const downloadCompensation = () => {
		dispatch(getBatchCompensations(
			institutionState.currentInstitution?.org_client_nbr,
			(data) => getPollJobStatus(data)));
	};

	const onValidateSuccess = (data) => {
		dispatch(compensationPollJob(
			data.poll_url,
			(response) => {
				if (response?.data?.status === 'complete') {
					setStagedFile(response?.data?.name);
					setValidateState(response?.data?.results);
					setFeedBackFile(`${ApiEndpoint.benAdmin}/employers/${institutionState.currentInstitution?.org_client_nbr}/batch_compensations/${data.poll_url?.split('=')[1]}`);
					setLoading(false);
				} else if (response?.data?.status === 'failed') {
					setfailedProcessing(true);
					setValidateState(null);
					setFeedBackFile(null);
					setUploadSuccess(false);
					setLoading(false);
				} else {
					onValidateSuccess(data);
				}
			}));
	};

	const validateAndUploadFile = (evt) => {
		if (evt.currentTarget?.files[0]) {
			const formData = new FormData();
			formData.append('file', evt.currentTarget?.files[0]);
			dispatch(validateCompensationFile(institutionState?.institutionClientNumber, formData, data => onValidateSuccess(data)));
			setLoading(true);
			setfailedProcessing(false);
		}
	};

	const createFileUpload = () => {
		const fileInput = document.createElement('input');
		fileInput.type = 'file';
		fileInput.accept = '.xls';
		fileInput.addEventListener('change', validateAndUploadFile);
		fileInput.click();
	};

	const cancelUpload = () => {
		setValidateState(null);
		setFeedBackFile(null);
	};

	const downloadValidateFile = () => {
		downloadFileFromApi(feedBackFile, token, validateState?.file_name || compensationFileName);
	};

	const onUploadSuccess = (data) => {
		dispatch(compensationPollJob(
			data.poll_url,
			(response) => {
				if (response?.data?.status === 'complete') {
					setValidateState(null);
					setFeedBackFile(null);
					setUploadSuccess(true);
					setLoading(false);
				} else if (response?.data?.status === 'failed') {
					setfailedProcessing(true);
					setValidateState(null);
					setFeedBackFile(null);
					setUploadSuccess(false);
					setLoading(false);
				} else {
					onUploadSuccess(data);
				}
			}));
	};

	const uploadValidatedDocument = () => {
		const formData = new FormData();
		formData.append('file', stagedFile);
		dispatch(uploadCompensationFile(institutionState?.institutionClientNumber, formData, data => onUploadSuccess(data)));
		setLoading(true);
		setfailedProcessing(false);
	};

	const renderDownloadUploadView = () => {
		return (
			<>
				{
					uploadSuccess &&
					<ContentSpacer>
						<Banner banner={{
							type: 'success',
							label: <div className='input-label' dangerouslySetInnerHTML={{ __html: settings?.submitSuccessMessage }} />,
						}} />
					</ContentSpacer>
				}
				{
					failedProcessing &&
					<ContentSpacer>
						<Banner banner={{
							type: 'error',
							label: <div className='input-label' dangerouslySetInnerHTML={{ __html: settings?.failedProcessingMessage || 'Failed Processing' }} />,
						}} />
					</ContentSpacer>
				}
				<PageHeader
					headerText={settings.compensationPageTitle}
					description={settings.compensationDescription}
				/>
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.compensationGpsTitle} contents={gpsSettings?.compensationGps} />
					</SideBarContent>
				</Hidden>
				<ContentSpacer>
					<ExternalLink newWindow linkIcon={<PictureAsPdfIcon />} linkUrl={settings?.instructionLink} linkText={settings?.instructionLinkText} />
				</ContentSpacer>
				<ContentSpacer>
					<button className={buttonClasses.secondaryButton} type='button' onClick={() => downloadCompensation()} >{settings?.downloadCompensationButtonText}</button>
				</ContentSpacer>

				<button className={buttonClasses.secondaryButton} type='button' onClick={() => createFileUpload()} >{settings?.uploadUpdatedCompensationButtonText}</button>

				<p className={`caption ${buttonClasses.buttonCaption}`}>
					{settings?.compensationHelperText}
				</p>
			</>
		);
	};

	const renderReviewView = () => {
		return (
			<>
				{
					validateState &&
					<ContentSpacer>
						<Banner banner={{
							type: hasValidateError ? 'warning' : 'success',
							label: <div className='input-label' dangerouslySetInnerHTML={{ __html: hasValidateError ? settings?.validateErrorMessage : settings?.validateSuccessMessage }} />,
						}} />
					</ContentSpacer>
				}
				{
					failedProcessing &&
					<ContentSpacer>
						<Banner banner={{
							type: 'error',
							label: <div className='input-label' dangerouslySetInnerHTML={{ __html: settings?.failedProcessingMessage || 'Failed Processing' }} />,
						}} />
					</ContentSpacer>
				}
				<PageHeader
					headerText={settings?.compensationReviewPageTitle}
					description={settings?.compensationReviewPageDescription}
				/>
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.compensationGpsTitle} contents={gpsSettings?.compensationGps} />
					</SideBarContent>
				</Hidden>
				<DividerSection removeDivider sectionText={settings?.compensationReviewSummaryTitle}>
					<LabelAndValue label={settings?.validLabel} value={validateState?.success} />
					<LabelAndValue label={settings?.unchangedLabel} value={validateState?.no_changes} />
					<LabelAndValue label={settings?.warningLabel} value={validateState?.warnings} />
					<LabelAndValue label={settings?.errorLabel} value={validateState?.errors} />
					<ContentSpacer>
						<WysiwygWithReactLink content={settings?.summaryDescription} externalHanlder={downloadValidateFile} />
					</ContentSpacer>
					<button className={buttonClasses.secondaryButton} type='button' onClick={() => createFileUpload()} >{settings?.selectUpdateFileButtonText}</button>
					<p className={`caption ${buttonClasses.buttonCaption}`}>
						{settings?.compensationHelperText}
					</p>
				</DividerSection>
				<ButtonWrapper flexEnd>
					<button className={buttonClasses.tertiaryButton} type='button' onClick={cancelUpload} >{settings?.cancelButtonText}</button>
					<button className={buttonClasses.primaryButton} type='button' onClick={uploadValidatedDocument} >{settings?.confirmButtonText}</button>
				</ButtonWrapper>
			</>
		);
	};

	if (loading) {
		return (<Loader />);
	}

	return (
		<>
			<PageContainer>
				<MainContent>
					{!validateState ? renderDownloadUploadView() : renderReviewView()}
				</MainContent>
				<Hidden smDown>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.compensationGpsTitle} contents={gpsSettings?.compensationGps} />
					</SideBarContent>
				</Hidden>
			</PageContainer>

		</>
	);
}

export default InstitutionCompensationApp;